import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useConnectionStatusPortal } from 'src/hooks/useConnectionStatusPortal';

interface ConnectionStatusIconPortalProps {
  portalTargetId: string;
  onTooltipChange: (text: string | null) => void;
  isOpen: boolean;
}

const ConnectionStatusIconPortal = ({ portalTargetId, onTooltipChange, isOpen }: ConnectionStatusIconPortalProps) => {
  const [ portalTarget, setPortalTarget ] = useState<HTMLElement | null>(null);
  const { connectionStatus, isLoading } = useConnectionStatusPortal();

  useEffect(() => {
    const findTarget = () => {
      const target = document.getElementById(portalTargetId);
      if (target) {
        setPortalTarget(target);
      }
    };

    findTarget();

    const timeoutId = setTimeout(findTarget, 100);

    const observer = new MutationObserver(findTarget);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [portalTargetId, isOpen]);

  if (!portalTarget || !isOpen) {
    return null;
  }

  const handleMouseEnter = () => {
    if (connectionStatus === false) {
      onTooltipChange("Organization Not Connected");
    }
  };

  const handleMouseLeave = () => {
    onTooltipChange(null);
  };

  if (isLoading) {
    return createPortal(
      <div className="w-4 h-4 border-4 border-brand-600 border-t-transparent rounded-full animate-spin"></div>,
      portalTarget
    );
  }

  if (connectionStatus === null || connectionStatus === true) {
    return null;
  }


  return createPortal(
    <div className="relative group">
      <ExclamationCircleIcon
        className="w-6 h-6 text-red-500"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
    </div>,
    portalTarget
  );
};


export default ConnectionStatusIconPortal;
